import React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RetryStats } from "@/types/pms";
import { formatRelativeTime } from "@/utils/dateUtils";

interface RetryStatsCardProps {
  stats: RetryStats;
}

export const RetryStatsCard = ({ stats }: RetryStatsCardProps) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("pms.stats.retry.title")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-muted-foreground text-sm">
          {t("pms.stats.retry.summary", {
            pending: stats.pendingCount,
            retrying: stats.retryingCount,
            failed: stats.failedCount,
          })}
        </p>

        {stats.recentFailures.length > 0 ? (
          <>
            <h4 className="text-sm font-medium">
              {t("pms.stats.retry.recentFailures")}
            </h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pms.stats.retry.eventType")}</TableHead>
                  <TableHead>{t("pms.stats.retry.error")}</TableHead>
                  <TableHead className="text-right">
                    {t("pms.stats.retry.attempts")}
                  </TableHead>
                  <TableHead>{t("pms.stats.retry.created")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recentFailures.map((failure, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">
                      {failure.eventType}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-[300px] truncate text-sm">
                      {failure.error}
                    </TableCell>
                    <TableCell className="text-right">
                      {failure.attempts}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatRelativeTime(failure.createdAt) || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">
            {t("pms.stats.retry.noFailures")}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
